import { Stack, Button } from "@mui/material";
import { motion } from "framer-motion";
import { COLORS, FONTS, fadeUpVariant } from "./Hero.styles";

/**
 * HeroButtons
 *
 * Preserves existing business logic exactly:
 *   - onTalkToExperts  → calls setDialogOpen(true) in the parent
 *   - onExploreSolutions → scrolls to #our-solutions
 *
 * Hover: subtle elevation (translateY + shadow), no glow/scale theatrics.
 * Keyboard accessible: native <button> via MUI Button, visible focus ring
 * inherited from MUI defaults, aria-label on each for clarity.
 */
export default function HeroButtons({ onTalkToExperts, onExploreSolutions }) {
  return (
    <motion.div {...fadeUpVariant(0.24, 12)}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing="14px"
        sx={{
          mt: { xs: "28px", md: "34px" },
          justifyContent: { xs: "center", lg: "flex-start" },
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        <Button
          variant="contained"
          size="large"
          onClick={onTalkToExperts}
          aria-label="Talk to our enterprise AI experts"
          disableElevation
          sx={{
            px: "30px",
            py: "13px",
            borderRadius: "10px",
            textTransform: "none",
            fontFamily: FONTS.body,
            fontWeight: 600,
            fontSize: "0.95rem",
            letterSpacing: "0.01em",
            background: COLORS.primary,
            color: "#ffffff",
            boxShadow: "0 1px 0 rgba(255,255,255,0.08) inset",
            transition: "transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease",
            "&:hover": {
              background: COLORS.primaryLight,
              transform: "translateY(-2px)",
              boxShadow: "0 8px 20px rgba(30,144,255,0.28)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "&:focus-visible": {
              outline: `2px solid ${COLORS.primary}`,
              outlineOffset: "2px",
            },
          }}
        >
          Talk to Experts
        </Button>

        <Button
          variant="outlined"
          size="large"
          onClick={onExploreSolutions}
          aria-label="Explore our enterprise AI solutions"
          sx={{
            px: "30px",
            py: "13px",
            borderRadius: "10px",
            textTransform: "none",
            fontFamily: FONTS.body,
            fontWeight: 600,
            fontSize: "0.95rem",
            letterSpacing: "0.01em",
            border: `1px solid ${COLORS.border}`,
            color: "rgba(255,255,255,0.85)",
            transition: "transform 0.18s ease, border-color 0.18s ease, background-color 0.18s ease",
            "&:hover": {
              border: `1px solid ${COLORS.borderHover}`,
              background: "rgba(30,144,255,0.05)",
              transform: "translateY(-2px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "&:focus-visible": {
              outline: `2px solid ${COLORS.primary}`,
              outlineOffset: "2px",
            },
          }}
        >
          Explore Solutions
        </Button>
      </Stack>
    </motion.div>
  );
}